import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  UploadCloud, FileText, Image, FileSpreadsheet, FileType, Box, File,
  Trash2, ExternalLink, Loader2, BadgeEuro, Sparkles, RefreshCw,
} from "lucide-react";
import { api } from "../../lib/api";

const ICONS = {
  pdf: FileText, immagine: Image, excel: FileSpreadsheet, documento: FileType,
  dwg: Box, primus: File, altro: File,
};
const CAT_LABELS = {
  pdf: "PDF", immagine: "Immagine", excel: "Excel", documento: "Documento",
  dwg: "DWG / CAD", primus: "PriMus DCF", altro: "File",
};

const fmtSize = (b) => (b > 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`);

export default function FilesTab({ offerId }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(false);
  const [importingOriginal, setImportingOriginal] = useState(null);
  const [analyzingFile, setAnalyzingFile] = useState(null);
  const [convertingFile, setConvertingFile] = useState(null);
  const inputRef = useRef();

  const load = () => api.listFiles(offerId).then(setFiles).catch(() => {});
  useEffect(() => {
    load();
    const timer = setInterval(load, 5000);
    return () => clearInterval(timer);
  }, [offerId]);

  const upload = async (fileList) => {
    setUploading(true);
    for (const f of Array.from(fileList)) {
      try {
        await api.uploadFile(offerId, f);
      } catch {
        toast.error(`Errore caricamento ${f.name}`);
      }
    }
    setUploading(false);
    toast.success("File caricati");
    load();
  };

  const importOriginal = async (f) => {
    setImportingOriginal(f.id);
    try {
      const preview = await api.previewOriginalOffer(offerId, f.id);
      const amount = Number(preview.total || 0).toLocaleString("it-IT", {minimumFractionDigits: 2, maximumFractionDigits: 2});
      const sheets = (preview.sheets || []).map(s => String(s.sheet || "").trim() + ": " + s.items + " voci").join("\n");
      const replacement = preview.existing_cme_items
        ? "ATTENZIONE: " + preview.existing_cme_items + " voci già presenti saranno sostituite."
        : "Il computo attuale è vuoto.";
      if (!window.confirm(
        "Excel letto correttamente.\n" + preview.items + " voci · € " + amount + "\n" + sheets + "\n\n" +
        replacement + "\n\nConfermi l'importazione dei prezzi originali?"
      )) return;
      const r = await api.importOriginalOffer(offerId, f.id);
      toast.success(r.imported + " voci importate · totale originale € " + amount);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Verifica/Importazione prezzi originali non riuscita"); }
    finally { setImportingOriginal(null); }
  };

  const analyzeFile = async (f) => {
    setAnalyzingFile(f.id);
    try {
      const r = await api.analyzeFile(offerId, f.id);
      const details = r.mode === "BIM_QUANTITY_ENGINE"
        ? `${r.element_count || 0} elementi BIM`
        : r.mode === "PDF_TAKEOFF_ENGINE"
          ? `${r.page_count || 0} pagine PDF`
          : r.mode === "CME_PREVIEW"
          ? String(r.recognized_items || 0) + " voci CME lette (nessuna importazione, usa Prezzi originali per confermare)"
          : r.mode === "CME_REIMPORT"
            ? `${r.imported || 0} voci computo`
            : "metadati aggiornati";
      toast.success(`File analizzato · ${details}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Analisi file non riuscita");
    } finally {
      setAnalyzingFile(null);
    }
  };

  const convertBim = async (f) => {
    setConvertingFile(f.id);
    try {
      await api.convertCadBimFile(offerId, f.id);
      toast.success("Conversione CAD/BIM avviata");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Conversione CAD/BIM non avviata");
    } finally {
      setConvertingFile(null);
    }
  };

  const del = async (fid) => {
    await api.deleteFile(fid);
    load();
  };



  return (
    <div className="space-y-5 fade-up">
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
        data-testid="file-upload-dropzone"
        className={`cursor-pointer border-2 border-dashed rounded-xl p-10 text-center transition-colors ${drag ? "border-orange-500 bg-orange-50" : "border-slate-300 bg-white hover:border-slate-400"}`}
      >
        <input ref={inputRef} type="file" multiple hidden onChange={(e) => upload(e.target.files)} data-testid="file-upload-dropzone-input" />
        {uploading ? <Loader2 className="w-8 h-8 text-orange-500 mx-auto animate-spin" /> : <UploadCloud className="w-8 h-8 text-slate-400 mx-auto" />}
        <p className="text-sm text-slate-600 mt-3 font-medium">Upload universale documenti + CAD/BIM</p>
        <p className="text-xs text-slate-400 mt-1">RVT · RFA · IFC · DWG · DGN · PDF · Excel · DOCX · Immagini · PriMus DCF</p>
        <p className="text-[11px] text-slate-400 mt-1">I modelli CAD/BIM vengono convertiti automaticamente sul server in dati strutturati e geometria web.</p>
      </div>

      {files.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-6">Nessun allegato caricato.</p>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-100">
          {files.map((f) => {
            const Icon = ICONS[f.category] || File;
            const isCadBim = /\.(rvt|rfa|ifc|dwg|dgn)$/i.test(f.original_filename || "");
            const conversionBusy = ["QUEUED", "RUNNING"].includes(f.conversion_status);
            const generatedByCadBim = f.source === "CADBIM_CONVERTER";
            return (
              <div key={f.id} data-testid={`file-row-${f.id}`} className="flex items-center gap-4 p-4">
                <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                  <Icon className="w-5 h-5 text-slate-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-slate-900 truncate">{f.original_filename}</div>
                  <div className="text-xs text-slate-400 flex items-center gap-2">
                    <span>{CAT_LABELS[f.category]}</span>·<span>{fmtSize(f.size)}</span>
                    {generatedByCadBim && <span>· generato CADBIM</span>}
                    {f.takeoff_status && <span className={f.takeoff_status === "FAILED" ? "text-red-600" : f.takeoff_status === "COMPLETED" ? "text-emerald-700" : "text-amber-700"}>Takeoff: {f.takeoff_status === "COMPLETED" ? "analizzato (da verificare)" : f.takeoff_status === "FAILED" ? "errore" : f.takeoff_status === "RUNNING" ? "in esecuzione" : "in coda"}</span>}
                  </div>
                </div>
                {isCadBim ? (
                  <span className={`text-[11px] font-medium px-2 py-1 rounded-md border ${
                    f.conversion_status === "COMPLETED" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                    f.conversion_status === "FAILED" ? "bg-red-50 text-red-700 border-red-200" :
                    conversionBusy ? "bg-amber-50 text-amber-700 border-amber-200" :
                    "bg-slate-50 text-slate-500 border-slate-200"
                  }`}>
                    {f.conversion_status === "COMPLETED" ? "Convertito" : f.conversion_status === "FAILED" ? "Errore conversione" : conversionBusy ? "Conversione BIM…" : "Da convertire"}
                  </span>
                ) : (
                  <span className={`text-[11px] font-medium px-2 py-1 rounded-md border ${f.ai_status === "analizzato" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : f.has_text ? "bg-sky-50 text-sky-700 border-sky-200" : "bg-slate-50 text-slate-500 border-slate-200"}`}>
                    {f.ai_status === "analizzato" ? "Analizzato" : f.has_text ? "Testo estraibile" : "Solo allegato"}
                  </span>
                )}
                {isCadBim && (
                  <button onClick={() => convertBim(f)} disabled={conversionBusy || convertingFile === f.id} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-violet-50 text-violet-700 border border-violet-200 hover:bg-violet-100 text-xs font-semibold disabled:opacity-60" title="Converte il file sul server in dati BIM strutturati e geometria web">
                    {conversionBusy || convertingFile === f.id ? <Loader2 className="w-4 h-4 animate-spin"/> : <RefreshCw className="w-4 h-4"/>}
                    {f.conversion_status === "COMPLETED" ? "Riconverti BIM" : "Converti BIM"}
                  </button>
                )}
                {!isCadBim && <button onClick={() => analyzeFile(f)} disabled={analyzingFile === f.id} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-orange-50 text-orange-700 border border-orange-200 hover:bg-orange-100 text-xs font-semibold disabled:opacity-60" title="Analizza solo questo file e aggiorna i dati collegati">
                  {analyzingFile === f.id ? <Loader2 className="w-4 h-4 animate-spin"/> : <Sparkles className="w-4 h-4"/>} Analizza file
                </button>}
                {f.category === "excel" && !generatedByCadBim && !f.bim_category && <button onClick={() => importOriginal(f)} disabled={importingOriginal === f.id} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-sky-50 text-sky-700 border border-sky-200 hover:bg-sky-100 text-xs font-semibold disabled:opacity-60" title="Importa quantità e prezzi dal file senza usare AI">{importingOriginal === f.id ? <Loader2 className="w-4 h-4 animate-spin"/> : <BadgeEuro className="w-4 h-4"/>} Prezzi originali</button>}
                <a href={api.fileUrl(f.id)} target="_blank" rel="noreferrer" data-testid={`file-open-${f.id}`} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"><ExternalLink className="w-4 h-4" /></a>
                <button onClick={() => del(f.id)} data-testid={`file-delete-${f.id}`} className="p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash2 className="w-4 h-4" /></button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
